/**
 * The word a user reads for a StockGroup.
 *
 * The stored noun is the group (`stock_group`, `StockGroupOut`, `/api/v1/stock-groups`) in the
 * database, the domain and on the wire, because one entity serves both personas. The label is
 * derived from the workspace and never stored: a Vendor files stock into a Location (a shelf, a
 * travel case); a Collector files cards into a Binder. See `CONTEXT.md` and
 * `docs/context/02-catalog-and-inventory.md`.
 *
 * The mapping lives in this one table rather than a ternary at each call site, so the stored noun
 * does not leak into user-facing strings such as "Create StockGroup" (TRO-727).
 */

/** Singular and plural forms of the noun, cased for titles and for mid-sentence prose. */
export type StockGroupNoun = {
  /** Title case, singular ("Location"), for button labels, headers and sheet titles. */
  one: string;
  /** Title case, plural ("Locations"), for section titles and nav rows. */
  many: string;
  /** Lower case, singular ("location"), for mid-sentence prose. */
  lowerOne: string;
  /** Lower case, plural ("locations"), for mid-sentence prose. */
  lowerMany: string;
};

export const VENDOR_STOCK_GROUP_NOUN: StockGroupNoun = {
  one: "Location",
  many: "Locations",
  lowerOne: "location",
  lowerMany: "locations",
};

export const COLLECTOR_STOCK_GROUP_NOUN: StockGroupNoun = {
  one: "Binder",
  many: "Binders",
  lowerOne: "binder",
  lowerMany: "binders",
};

/**
 * Returns the noun for a workspace. Vendor is the default, as in the desktop shell's navigation:
 * `isCollectorWorkspace` reads an unanswered `/me` as not a collector, so a query in flight or a
 * workspace switch shows the Vendor word instead of flickering to the Collector one and back.
 */

export function stockGroupNoun(collector: boolean): StockGroupNoun {
  return collector ? COLLECTOR_STOCK_GROUP_NOUN : VENDOR_STOCK_GROUP_NOUN;
}
